import { forwardRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { FilledTextFieldProps } from "@mui/material/TextField";
import SearchIconV2 from "components/Icons/SearchIconV2";
import { StyledTextField } from "./components";
import XIcon from "components/Icons/XIcon";
import IconButton from "components/IconButton";
import { styled } from "@mui/material";

const ClearIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.common.white,
  backgroundColor: theme.palette.grey[60],
  "&:hover": { backgroundColor: theme.palette.grey[100] },
}));

export interface SearchProps extends Omit<FilledTextFieldProps, "variant"> {
  onClear: () => void;
}

const Search = forwardRef<HTMLDivElement, SearchProps>((props, ref) => {
  const { slotProps, onClear, value, ...rest } = props;
  const { t } = useTranslation();

  const memoSlotProps = useMemo(() => {
    return {
      ...slotProps,
      input: {
        startAdornment: <SearchIconV2 fontSize="small" />,
        endAdornment: (
          <ClearIconButton
            size="extraSmall"
            onClick={onClear}
            title={t("global.clear_label")}
            aria-label={t("global.clear_label")}
            sx={{ visibility: value ? "visible" : "hidden" }}
          >
            <XIcon fontSize="extraSmall" />
          </ClearIconButton>
        ),
        ...slotProps?.input,
      },
    };
  }, [onClear, slotProps, t, value]);

  return (
    <StyledTextField
      ref={ref}
      label={t("global.search_text")}
      placeholder={t("global.search_text")}
      color="primary"
      value={value}
      slotProps={memoSlotProps}
      type="search"
      {...rest}
      variant="filled"
    />
  );
});

export default Search;
